import { useMemo, useState } from 'react';
import { Monitor, Smartphone, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { contestoAnteprima, DESTINATARIO_ESEMPIO } from '../../lib/email/renderContext';
import { renderPerDestinatario } from '@email/index.ts';
import type { DocumentoEmail } from '@email/types.ts';

interface EmailPreviewProps {
  documento: DocumentoEmail;
  oggetto: string;
  preheader?: string;
  /** Dentro una finestra ad altezza fissa l'anteprima riempie lo spazio che
   *  resta, invece di essere alta 620px e costringere a scorrere due volte. */
  adatta?: boolean;
}

/**
 * L'anteprima della email.
 *
 * Non e' una simulazione: e' lo stesso renderer che compone le mail vere, con
 * gli stessi blocchi e lo stesso involucro. Quello che si vede qui e' quello
 * che arriva, a parte i link di disiscrizione, che in anteprima restano
 * disattivati per non uscire dalla lista per sbaglio mentre si lavora.
 *
 * Si usa un iframe con `sandbox` perche' il contenuto e' HTML composto
 * dall'utente: anche se il renderer lo mette gia' in sicurezza, farlo girare
 * dentro un riquadro isolato e' una precauzione che non costa niente.
 */
export function EmailPreview({ documento, oggetto, preheader, adatta }: EmailPreviewProps) {
  const [larghezza, setLarghezza] = useState<'desktop' | 'telefono'>('desktop');

  const reso = useMemo(
    () =>
      renderPerDestinatario(
        documento,
        oggetto || '(nessun oggetto)',
        DESTINATARIO_ESEMPIO,
        contestoAnteprima(),
        preheader
      ),
    [documento, oggetto, preheader]
  );

  return (
    <div className={cn('space-y-3', adatta && 'flex flex-col h-full min-h-0')}>
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
            {adatta ? 'Come arriva a Mario Rossi' : 'Oggetto'}
          </p>
          <p className="text-sm font-semibold text-gray-900 truncate">{reso.oggetto}</p>
          {preheader && <p className="text-xs text-neutral-500 truncate">{preheader}</p>}
        </div>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden flex-shrink-0">
          {([
            { v: 'desktop', icona: Monitor, titolo: 'Come si vede da computer' },
            { v: 'telefono', icona: Smartphone, titolo: 'Come si vede da telefono' },
          ] as const).map(({ v, icona: Icona, titolo }) => (
            <button
              key={v}
              type="button"
              title={titolo}
              onClick={() => setLarghezza(v)}
              className={cn(
                'px-2.5 py-1.5 transition-colors',
                larghezza === v ? 'bg-primary-600 text-white' : 'bg-white text-neutral-500 hover:bg-gray-50'
              )}
            >
              <Icona className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>

      {reso.vuoti.length > 0 && (
        <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Questi dati mancano e non hanno un valore di riserva:{' '}
            <strong>{reso.vuoti.join(', ')}</strong>. Chi non li ha compilati ricevera' uno spazio
            vuoto al loro posto.
          </p>
        </div>
      )}

      {reso.sconosciuti.length > 0 && (
        <div className="flex gap-2.5 rounded-lg border border-red-200 bg-red-50 p-2.5">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-900 leading-relaxed">
            Questi segnaposto non esistono e verranno cancellati:{' '}
            <strong>{reso.sconosciuti.join(', ')}</strong>.
          </p>
        </div>
      )}

      <div
        className={cn(
          'rounded-lg border border-gray-200 overflow-hidden bg-gray-100',
          adatta && 'flex-1 min-h-0'
        )}
      >
        <iframe
          title="Anteprima della email"
          srcDoc={reso.html}
          sandbox="allow-same-origin"
          className={cn(
            'w-full border-0 bg-gray-100 transition-all duration-200 mx-auto block',
            larghezza === 'telefono' ? 'max-w-[400px]' : 'max-w-full',
            adatta && 'h-full'
          )}
          style={adatta ? undefined : { height: 620 }}
        />
      </div>
    </div>
  );
}
